import React, { useEffect, useState } from 'react';
import { useSwipeGesture } from '../../hooks/useSwipeGesture';
import { CONTENEDOR, PantallaHeader, BotonPrimario, Pestanas, PAGINA, BotonFlotante } from '../ui/Pantalla';
import { toast } from 'react-hot-toast';
import { getUsuarios, crearUsuario, editarUsuario, cambiarPassword, eliminarUsuario } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import AvatarTecnico from '../ui/AvatarTecnico';
import { PALETA_TECNICO, setColoresTecnicos } from '../../utils/estados';
import { LuShieldCheck, LuWrench, LuKey, LuPencil, LuLock, LuCircleCheck, LuTrash2, LuEyeOff, LuEye, LuBuilding2 } from 'react-icons/lu';

const ROL_LABEL = { ADMIN: 'Administrador', TECNICO: 'Técnico', EMPRESA: 'Empresa' };
const ROL_COLOR = {
    ADMIN:   'bg-[#D13A28]/10 text-[#D13A28] dark:bg-[#E8422F]/10 dark:text-[#E8422F]',
    TECNICO: 'bg-[#D48800]/10 text-[#D48800] dark:bg-[#F0A500]/10 dark:text-[#F0A500]',
    EMPRESA: 'bg-[#2563EB]/10 text-[#2563EB] dark:bg-[#60A5FA]/10 dark:text-[#60A5FA]',
};

const FORM_VACIO = { nombre: '', username: '', password: '', passwordConfirm: '', rol: 'TECNICO', telefono: '', whatsapp: '', clienteId: '', sedeId: '' };

export default function UsuariosManager() {
    const { usuario: usuarioActual } = useAuth();
    const [usuarios, setUsuarios]     = useState([]);
    const [cargando, setCargando]     = useState(true);
    const [filtroRol, setFiltroRol]   = useState('TODOS');
    const [confirmEliminar, setConfirmEliminar] = useState(null); // null | usuario

    // Las condiciones del PDF se mudaron a Más › Configuración (3-oct-2026).
    // Los inactivos quedan ocultos hasta tocar "Ver inactivos".
    const [verInactivos, setVerInactivos] = useState(false);
    const [colorDe, setColorDe] = useState(null);
    // Portal Empresa (7-oct-2026): usuarios de empresa → a qué cliente pertenecen
    const [empresaDe, setEmpresaDe] = useState({});
    const [clientes, setClientes] = useState([]);
    const guardarColor = async (u, color) => {
        try {
            await api.put(`/admin/usuarios/${u.id}/color`, { color });
            const nuevos = usuarios.map(x => (x.id === u.id ? { ...x, color: color || null } : x));
            setUsuarios(nuevos);
            setColoresTecnicos(nuevos.filter(x => x.activo !== false));
            setColorDe(null);
            toast.success('Color guardado');
        } catch { toast.error('No se pudo guardar el color'); }
    };

    // Modal crear/editar
    const [modal, setModal]           = useState(null); // null | 'crear' | usuario
    const [form, setForm]             = useState(FORM_VACIO);
    const [guardando, setGuardando]   = useState(false);

    // Modal cambiar clave
    const [modalPass, setModalPass]   = useState(null); // null | usuario
    const [nuevaClave, setNuevaClave] = useState('');

    // Visibilidad de claves
    const [verClave, setVerClave]           = useState(false);
    const [verClaveConfirm, setVerClaveConfirm] = useState(false);
    const [verNuevaClave, setVerNuevaClave] = useState(false);

    const cargar = async () => {
        setCargando(true);
        try {
            // Bajo del testeo: se esperan los dos juntos; antes, si abrías un usuario empresa
            // antes de que llegaran sus datos, el cliente aparecía vacío y se podía guardar así
            const [r, x] = await Promise.all([getUsuarios(), api.get('/pedidos-empresa/usuarios').catch(() => ({ data: [] }))]);
            const m = {}; (x.data || []).forEach(e => { m[e.id] = e; }); setEmpresaDe(m);
            setUsuarios(r.data);
        } catch { toast.error('Error al cargar usuarios'); }
        finally { setCargando(false); }
    };

    useEffect(() => { cargar(); }, []);

    const abrirCrear = () => { setForm(FORM_VACIO); setModal('crear'); setVerClave(false); setVerClaveConfirm(false); };
    const cargarClientes = () => {
        if (clientes.length) return;
        api.get('/clientes', { params: { size: 1000 } }).then(r => setClientes((Array.isArray(r.data) ? r.data : r.data?.content || [])
            .slice().sort((a, b) => String(a.nombre).localeCompare(String(b.nombre))))).catch(() => {});
    };
    const abrirEditar = (u) => {
        if (u.rol === 'EMPRESA') cargarClientes();
        setForm({ nombre: u.nombre, username: u.username, password: '', rol: u.rol, telefono: u.telefono || '', whatsapp: u.whatsapp || '', clienteId: empresaDe[u.id]?.clienteId || '', sedeId: empresaDe[u.id]?.sedeId || '' });
        setModal(u);
    };

    const guardar = async () => {
        if (!form.nombre.trim() || !form.username.trim()) {
            toast.error('Nombre y usuario son obligatorios'); return;
        }
        if (modal === 'crear' && !form.password.trim()) {
            toast.error('La contraseña es obligatoria'); return;
        }
        if (modal === 'crear' && form.password.length < 6) {
            toast.error('Mínimo 6 caracteres'); return;
        }
        if (modal === 'crear' && form.password !== form.passwordConfirm) {
            toast.error('Las contraseñas no coinciden'); return;
        }
        if (form.rol === 'EMPRESA' && !form.clienteId) {
            toast.error('Elegí a qué cliente pertenece'); return;
        }
        const clienteId = form.rol === 'EMPRESA' ? Number(form.clienteId) : null;
        const sedeId = form.rol === 'EMPRESA' && form.sedeId ? Number(form.sedeId) : null;
        setGuardando(true);
        try {
            if (modal === 'crear') {
                await crearUsuario({ nombre: form.nombre, username: form.username, password: form.password, rol: form.rol, telefono: form.telefono || null, whatsapp: form.whatsapp || null, clienteId, sedeId });
                toast.success('Usuario creado');
            } else {
                await editarUsuario(modal.id, { nombre: form.nombre, rol: form.rol, activo: modal.activo, telefono: form.telefono || null, whatsapp: form.whatsapp || null, clienteId, sedeId });
                toast.success('Usuario actualizado');
            }
            setModal(null);
            cargar();
        } catch (e) {
            if (e.response?.status === 409) {
                toast.error(e.response?.data?.mensaje || 'El usuario ya existe');
            } else {
                toast.error('Error al guardar');
            }
        } finally { setGuardando(false); }
    };

    const toggleActivo = async (u) => {
        try {
            await editarUsuario(u.id, { nombre: u.nombre, rol: u.rol, activo: !u.activo, telefono: u.telefono || null, whatsapp: u.whatsapp || null, clienteId: empresaDe[u.id]?.clienteId || null, sedeId: empresaDe[u.id]?.sedeId || null });
            toast.success(u.activo ? 'Usuario desactivado' : 'Usuario activado');
            cargar();
        } catch (e) {
            toast.error(e.response?.data?.mensaje || 'Error al actualizar');
        }
    };

    const eliminar = async () => {
        try {
            await eliminarUsuario(confirmEliminar.id);
            toast.success('Usuario eliminado');
            setConfirmEliminar(null);
            cargar();
        } catch (e) {
            const msg = e.response?.data?.mensaje || 'Error al eliminar';
            toast.error(msg);
            setConfirmEliminar(null);
        }
    };

    const guardarClave = async () => {
        if (!nuevaClave || nuevaClave.length < 6) {
            toast.error('Mínimo 6 caracteres'); return;
        }
        try {
            await cambiarPassword(modalPass.id, { nuevaPassword: nuevaClave });
            toast.success('Contraseña cambiada');
            setModalPass(null); setNuevaClave('');
        } catch { toast.error('Error al cambiar contraseña'); }
    };

    const inactivos = usuarios.filter(u => !u.activo).length;
    const usuariosFiltrados = (filtroRol === 'TODOS' ? usuarios : usuarios.filter(u => u.rol === filtroRol))
        .filter(u => verInactivos || u.activo);

    const swipeHandlers = useSwipeGesture(['TODOS', 'ADMIN', 'TECNICO', 'EMPRESA'], filtroRol, setFiltroRol);

    return (
        <div className={PAGINA} {...swipeHandlers}>
            <BotonFlotante onClick={abrirCrear} label="Nuevo usuario" />

            <div className={CONTENEDOR}>
                <PantallaHeader titulo="Usuarios" subtitulo="Quién entra al sistema y con qué permisos"
                    accion={<BotonPrimario onClick={abrirCrear}>Nuevo</BotonPrimario>} />

                <Pestanas activo={filtroRol} onChange={setFiltroRol}
                    items={['TODOS', 'ADMIN', 'TECNICO', 'EMPRESA'].map(rol => ({
                        id: rol,
                        label: rol === 'TODOS' ? 'Todos' : ROL_LABEL[rol],
                        count: rol === 'TODOS' ? usuarios.length : usuarios.filter(u => u.rol === rol).length,
                    }))} />


                {inactivos > 0 && (
                    <button type="button" onClick={() => setVerInactivos(v => !v)}
                        className="text-caption font-bold text-muted underline">
                        {verInactivos ? 'Ocultar inactivos' : `Ver inactivos (${inactivos})`}
                    </button>
                )}

                {/* LISTA */}
                {cargando ? (
                    <div className="text-center py-16 font-bold text-muted">Cargando...</div>
                ) : usuariosFiltrados.length === 0 ? (
                    <div className="text-center py-16 rounded-2xl bg-card text-muted font-bold">
                        Sin usuarios en esta categoría
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        {usuariosFiltrados.map(u => (
                            <div key={u.id}
                                 className={`rounded-2xl bg-card overflow-hidden border ${
                                     u.activo
                                         ? 'border-black/[0.07] dark:border-white/[0.07]'
                                         : 'border-dashed border-black/[0.15] dark:border-white/[0.15] opacity-60'
                                 }`}>
                                <div className="p-4 flex items-center justify-between gap-3">
                                    {/* Avatar con su color: tocándolo se elige otro (3-oct-2026) */}
                                    <button type="button" onClick={() => setColorDe(c => (c === u.id ? null : u.id))} title="Cambiar color"
                                        className="relative shrink-0 active:scale-95">
                                        <AvatarTecnico nombre={u.nombre} size={40} />
                                        <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-card border border-black/10 dark:border-white/10 flex items-center justify-center text-muted">
                                            {u.rol === 'ADMIN' ? <LuShieldCheck size={11} /> : u.rol === 'EMPRESA' ? <LuBuilding2 size={11} /> : <LuWrench size={11} />}
                                        </span>
                                    </button>
                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="font-black text-body-lg text-ink">{u.nombre}</p>
                                            <span className={`text-label font-bold px-2 py-0.5 rounded-md uppercase ${ROL_COLOR[u.rol]}`}>
                                                {ROL_LABEL[u.rol]}
                                            </span>
                                            {!u.activo && (
                                                <span className="text-label font-bold px-2 py-0.5 rounded-md uppercase bg-muted/20 text-muted">
                                                    Inactivo
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-caption text-muted mt-0.5">@{u.username}{u.telefono ? `  ·  ${u.telefono}` : ''}</p>
                                        {u.rol === 'EMPRESA' && <p className="text-caption font-bold text-secondary mt-0.5">Portal de {empresaDe[u.id]?.clienteNombre || '—'}{empresaDe[u.id]?.sedeNombre ? ` · solo ${empresaDe[u.id].sedeNombre}` : ''}</p>}
                                    </div>
                                    {/* Acciones */}
                                    <div className="flex gap-2 shrink-0">
                                        <button
                                            onClick={() => { setModalPass(u); setNuevaClave(''); }}
                                            title="Cambiar contraseña"
                                            className="w-9 h-9 rounded-xl flex items-center justify-center text-sm bg-chip active:scale-90 transition-all"
                                        ><LuKey size={15} /></button>
                                        <button
                                            onClick={() => abrirEditar(u)}
                                            title="Editar"
                                            className="w-9 h-9 rounded-xl flex items-center justify-center text-sm bg-chip active:scale-90 transition-all"
                                        ><LuPencil size={15} /></button>
                                        <button
                                            onClick={() => toggleActivo(u)}
                                            title={u.activo ? 'Desactivar' : 'Activar'}
                                            className="w-9 h-9 rounded-xl flex items-center justify-center text-sm bg-chip active:scale-90 transition-all"
                                        >{u.activo ? <LuLock size={15} /> : <LuCircleCheck size={15} />}</button>
                                        {usuarioActual?.id !== u.id && (
                                            <button
                                                onClick={() => setConfirmEliminar(u)}
                                                title="Eliminar"
                                                className="w-9 h-9 rounded-xl flex items-center justify-center text-sm bg-[#D13A28]/10 dark:bg-[#E8422F]/10 active:scale-90 transition-all"
                                            ><LuTrash2 size={15} /></button>
                                        )}
                                    </div>
                                </div>
                                {colorDe === u.id && (
                                    <div className="px-4 pb-4 -mt-1">
                                        <p className="text-label font-bold text-muted uppercase tracking-wider mb-2">Color de {u.nombre.split(' ')[0]}</p>
                                        <div className="flex flex-wrap items-center gap-2">
                                            {PALETA_TECNICO.map(c => (
                                                <button key={c} type="button" onClick={() => guardarColor(u, c)} aria-label={`Color ${c}`}
                                                    className={`w-9 h-9 rounded-full active:scale-90 ${(u.color || '').toUpperCase() === c ? 'ring-[3px] ring-offset-2 ring-ink ring-offset-card' : ''}`}
                                                    style={{ background: c }} />
                                            ))}
                                            <button type="button" onClick={() => guardarColor(u, '')}
                                                className={`h-9 px-3 rounded-full text-label font-bold bg-chip ${!u.color ? 'text-ink ring-2 ring-ink' : 'text-muted'}`}>Automático</button>
                                        </div>
                                        <p className="text-caption text-muted mt-2">Se ve como un círculo con sus iniciales en toda la app.</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* MODAL CREAR / EDITAR */}
            {modal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm md:pl-[calc(var(--modal-sb,0px)+1.5rem)] md:pr-6">
                    <div className="w-full max-w-sm rounded-3xl p-6 bg-card shadow-2xl space-y-4">
                        <h3 className="text-title font-black text-ink">
                            {modal === 'crear' ? 'Nuevo usuario' : `Editar: ${modal.nombre}`}
                        </h3>

                        <div className="space-y-3">
                            <div>
                                <label className="text-label font-bold text-muted uppercase tracking-wider">Nombre completo</label>
                                <input
                                    className="mt-1 w-full h-10 px-3 rounded-xl text-body font-bold bg-chip text-ink border border-black/[0.08] dark:border-white/[0.08] outline-none"
                                    value={form.nombre}
                                    onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                                    placeholder="Ej: Juan Pérez"
                                />
                            </div>
                            {modal === 'crear' && (
                                <>
                                    <div>
                                        <label className="text-label font-bold text-muted uppercase tracking-wider">Usuario (login)</label>
                                        <input
                                            className="mt-1 w-full h-10 px-3 rounded-xl text-body font-bold bg-chip text-ink border border-black/[0.08] dark:border-white/[0.08] outline-none"
                                            value={form.username}
                                            // Mismo fix que en el login: un usuario creado con un
                                            // espacio de más después no puede loguearse con lo que
                                            // ve escrito, sin ninguna pista de por qué.
                                            onChange={e => setForm(f => ({ ...f, username: e.target.value.replace(/\s+/g, '') }))}
                                            placeholder="Ej: juan"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-label font-bold text-muted uppercase tracking-wider">Contraseña</label>
                                        <div className="relative mt-1">
                                            <input
                                                type={verClave ? 'text' : 'password'}
                                                className="w-full h-10 px-3 pr-9 rounded-xl text-body font-bold bg-chip text-ink border border-black/[0.08] dark:border-white/[0.08] outline-none"
                                                value={form.password}
                                                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                                                placeholder="Mínimo 6 caracteres"
                                            />
                                            <button type="button" onClick={() => setVerClave(v => !v)} tabIndex={-1}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted text-label">
                                                {verClave ? <LuEyeOff size={14} /> : <LuEye size={14} />}
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-label font-bold text-muted uppercase tracking-wider">Confirmar contraseña</label>
                                        <div className="relative mt-1">
                                            <input
                                                type={verClaveConfirm ? 'text' : 'password'}
                                                className={`w-full h-10 px-3 pr-9 rounded-xl text-body font-bold bg-chip text-ink outline-none border ${
                                                    form.passwordConfirm && form.password !== form.passwordConfirm
                                                        ? 'border-brand-red'
                                                        : 'border-black/[0.08] dark:border-white/[0.08]'
                                                }`}
                                                value={form.passwordConfirm}
                                                onChange={e => setForm(f => ({ ...f, passwordConfirm: e.target.value }))}
                                                placeholder="Repetir contraseña"
                                            />
                                            <button type="button" onClick={() => setVerClaveConfirm(v => !v)} tabIndex={-1}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted text-label">
                                                {verClaveConfirm ? <LuEyeOff size={14} /> : <LuEye size={14} />}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-label font-bold text-muted uppercase tracking-wider">Teléfono</label>
                                    <input
                                        className="mt-1 w-full h-10 px-3 rounded-xl text-body font-bold bg-chip text-ink border border-black/[0.08] dark:border-white/[0.08] outline-none"
                                        value={form.telefono}
                                        onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))}
                                        placeholder="(011) XXXX-XXXX"
                                    />
                                </div>
                                <div>
                                    <label className="text-label font-bold text-muted uppercase tracking-wider">WhatsApp</label>
                                    <input
                                        className="mt-1 w-full h-10 px-3 rounded-xl text-body font-bold bg-chip text-ink border border-black/[0.08] dark:border-white/[0.08] outline-none"
                                        value={form.whatsapp}
                                        onChange={e => setForm(f => ({ ...f, whatsapp: e.target.value }))}
                                        placeholder="11 XXXX-XXXX"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-label font-bold text-muted uppercase tracking-wider">Rol</label>
                                <select
                                    className="mt-1 w-full h-10 px-3 rounded-xl text-body font-bold bg-chip text-ink border border-black/[0.08] dark:border-white/[0.08] outline-none"
                                    value={form.rol}
                                    onChange={e => {
                                        const rol = e.target.value;
                                        setForm(f => ({ ...f, rol }));
                                        if (rol === 'EMPRESA') cargarClientes();
                                    }}
                                >
                                    <option value="TECNICO">Técnico</option>
                                    <option value="ADMIN">Administrador</option>
                                    <option value="EMPRESA">Empresa (portal de pedidos)</option>
                                </select>
                            </div>
                            {form.rol === 'EMPRESA' && (
                                <div>
                                    <label className="text-label font-bold text-muted uppercase tracking-wider">Cliente</label>
                                    <select
                                        className="mt-1 w-full h-10 px-3 rounded-xl text-body font-bold bg-chip text-ink border border-black/[0.08] dark:border-white/[0.08] outline-none"
                                        value={form.clienteId}
                                        onChange={e => setForm(f => ({ ...f, clienteId: e.target.value, sedeId: '' }))}
                                    >
                                        <option value="">Elegí el cliente…</option>
                                        {form.clienteId && !clientes.some(c => String(c.id) === String(form.clienteId)) && (
                                            <option value={form.clienteId}>{empresaDe[modal?.id]?.clienteNombre || `Cliente #${form.clienteId}`}</option>
                                        )}
                                        {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                                    </select>
                                    <p className="text-caption text-muted mt-1">Solo va a ver sus pedidos y los lugares de ese cliente. Nada de precios ni del resto del sistema.</p>
                                    {form.clienteId && <SelectorLugar clienteId={form.clienteId} value={form.sedeId} onChange={v => setForm(f => ({ ...f, sedeId: v }))} />}
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3 pt-2">
                            <button
                                onClick={() => setModal(null)}
                                className="flex-1 h-10 rounded-xl font-bold text-label uppercase bg-chip text-secondary"
                            >Cancelar</button>
                            <button
                                onClick={guardar}
                                disabled={guardando}
                                className="flex-1 h-10 rounded-xl font-bold text-label uppercase text-white bg-brand-red disabled:opacity-50"
                            >{guardando ? 'Guardando...' : 'Guardar'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL CONFIRMAR ELIMINACIÓN */}
            {confirmEliminar && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm md:pl-[calc(var(--modal-sb,0px)+1.5rem)] md:pr-6">
                    <div className="w-full max-w-sm rounded-3xl p-6 bg-card shadow-2xl space-y-4">
                        <h3 className="text-title font-black text-ink">
                            Eliminar usuario
                        </h3>
                        <p className="text-body text-secondary">
                            ¿Seguro que querés eliminar a <span className="font-bold text-ink">{confirmEliminar.nombre}</span>? Esta acción no se puede deshacer.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setConfirmEliminar(null)}
                                className="flex-1 h-10 rounded-xl font-bold text-label uppercase bg-chip text-secondary"
                            >Cancelar</button>
                            <button
                                onClick={eliminar}
                                className="flex-1 h-10 rounded-xl font-bold text-label uppercase text-white bg-brand-red"
                            >Eliminar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL CAMBIAR CONTRASEÑA */}
            {modalPass && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm md:pl-[calc(var(--modal-sb,0px)+1.5rem)] md:pr-6">
                    <div className="w-full max-w-sm rounded-3xl p-6 bg-card shadow-2xl space-y-4">
                        <h3 className="text-title font-black text-ink">
                            Cambiar clave: {modalPass.nombre}
                        </h3>
                        <div>
                            <label className="text-label font-bold text-muted uppercase tracking-wider">Nueva contraseña</label>
                            <div className="relative mt-1">
                                <input
                                    type={verNuevaClave ? 'text' : 'password'}
                                    className="w-full h-10 px-3 pr-9 rounded-xl text-body font-bold bg-chip text-ink border border-black/[0.08] dark:border-white/[0.08] outline-none"
                                    value={nuevaClave}
                                    onChange={e => setNuevaClave(e.target.value)}
                                    placeholder="Mínimo 6 caracteres"
                                />
                                <button type="button" onClick={() => setVerNuevaClave(v => !v)} tabIndex={-1}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted text-label">
                                    {verNuevaClave ? <LuEyeOff size={14} /> : <LuEye size={14} />}
                                </button>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => { setModalPass(null); setNuevaClave(''); }}
                                className="flex-1 h-10 rounded-xl font-bold text-label uppercase bg-chip text-secondary"
                            >Cancelar</button>
                            <button
                                onClick={guardarClave}
                                className="flex-1 h-10 rounded-xl font-bold text-label uppercase text-white bg-brand-red"
                            >Guardar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Encargado de un lugar (8-oct-2026): si se elige una sede, ese usuario solo ve y pide
// para ese lugar. "Toda la empresa" = ve todo (y es quien aprueba el resumen del mes).
function SelectorLugar({ clienteId, value, onChange }) {
    const [sedes, setSedes] = useState([]);
    useEffect(() => {
        api.get(`/sedes/cliente/${clienteId}`).then(r => setSedes(Array.isArray(r.data) ? r.data : (r.data?.content || []))).catch(() => setSedes([]));
    }, [clienteId]);
    return (
        <div className="mt-3">
            <label className="text-label font-bold text-muted uppercase tracking-wider">Qué lugares ve</label>
            <select className="mt-1 w-full h-10 px-3 rounded-xl text-body font-bold bg-chip text-ink border border-black/[0.08] dark:border-white/[0.08] outline-none"
                value={value || ''} onChange={e => onChange(e.target.value)}>
                <option value="">Toda la empresa (responsable)</option>
                {sedes.filter(s => s.activa !== false).map(s => <option key={s.id} value={s.id}>Solo {s.nombreSede || s.direccion}</option>)}
            </select>
            <p className="text-caption text-muted mt-1">El encargado de un lugar solo ve los pedidos, equipos y avisos de ese lugar.</p>
        </div>
    );
}
